import styled from 'styled-components'
import { TdsButton, TdsIcon } from '@scania/tegel-react'
import { useAppSelector } from '../../../store/hooks'
import { useEffect, useState } from 'react'
import { getMarketDenomination } from '../../../store/sessionDataSlice'
import {
  getLatestConfigChangeResult,
  getTruckInfoState,
} from '../../../store/truckInfoSlice'
import { CustomScrollbarMixin } from '../../../css/CustomScrollbarMixin'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import { Size2d } from '../../../types/Size2d'
import { FAMILY_ID_APPLICATION } from '../../../utils/constants'
import { MiniSummary } from './Minsummary'
import { useRfqEysButtonState } from '../RfqEysButtonState'
import {
  buildRelativeAllViewPage,
  buildRelativeGvPage,
} from '../../../utils/UrlBuilders'
import { getUrlParametersToString } from '../../../utils/getUrlParametersToString'
import { useNavigate } from 'react-router-dom'
import { getMarketLanguageState } from '../../../store/appSlice'
import { TestElementTypeId } from '../../../types/TestAttributeId'

const SpecificationRoot = styled.section`
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    height: unset;
  }
`

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-right: 16px;
  margin-bottom: 16px;
  gap: 6px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin-right: 0px;
  }
`

const Header = styled.h3`
  max-width: 60%;
  word-break: break-word;
`

const ApplicationWrapper = styled.div`
  background-color: var(--tds-grey-200);
  border-radius: 16px;
  padding: 6px 8px 6px 8px;
  height: fit-content;
`

const SpecificationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin: 8px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin: 0px;
  }
`

const ScrollContainer = styled.div`
  ${CustomScrollbarMixin}
  flex: 1 1 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 8px;
  padding-right: 40px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    overflow: unset;
    padding-right: 4px;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 0px;
  justify-content: center;

  button {
    min-width: 200px;
    display: flex;
    justify-content: center;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    position: sticky;
    bottom: 0px;
    flex-direction: column;
    background-color: white;
    border-top: 1px solid var(--tds-grey-500);
    width: 100vw;
    margin-left: -24px;
    padding-top: 16px;
    padding-bottom: 16px;
    padding-right: 32px;
    padding-left: 32px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px) {
    flex-wrap: wrap;
  }
`

export interface CurrentConfigSpecificationProps {
  handleRfqOrEysClick: () => void
  parentSize: Size2d
}

export function OverviewSpecification({
  handleRfqOrEysClick,
  parentSize,
}: CurrentConfigSpecificationProps): JSX.Element {
  const navigate = useNavigate()
  const miniSummaryState = useAppSelector(
    getLatestConfigChangeResult,
  )?.miniSummary
  const marketDenomination = useAppSelector(getMarketDenomination)
  const truckInfo = useAppSelector(getTruckInfoState)
  const isFactoryModel = truckInfo?.isFactoryModel
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const t2 = useTextsV2()
  const { rfqOrEysButtonHidden, rfqOrEysButtonText, rfqOrEysButtonEnabled } =
    useRfqEysButtonState()
  const [application, setApplication] = useState<string>('')

  const handleConfigurationClick = () => {
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    if (isFactoryModel) {
      const relativeUrl = buildRelativeAllViewPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)
    } else {
      const relativeUrl = buildRelativeGvPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)
    }
  }

  // Get application
  useEffect(() => {
    miniSummaryState?.items?.forEach((element) => {
      if (element.itemId.startsWith(FAMILY_ID_APPLICATION)) {
        setApplication(element.itemText)
        return
      }
    })
  }, [miniSummaryState])

  return (
    <SpecificationRoot>
      <SpecificationWrapper
        data-test-element-type={TestElementTypeId.OverviewSummary}
      >
        <HeaderWrapper>
          <Header>
            {truckInfo?.savedAsName ||
              truckInfo?.factoryModelName ||
              marketDenomination}
          </Header>
          <ApplicationWrapper>{application}</ApplicationWrapper>
        </HeaderWrapper>
        <ScrollContainer>
          <MiniSummary showPackages={true} />
        </ScrollContainer>
      </SpecificationWrapper>
      <ButtonsWrapper>
        {!rfqOrEysButtonHidden && (
          <TdsButton
            variant="primary"
            text={rfqOrEysButtonText}
            size={
              parentSize.height < BreakpointHeightPx.Large ||
              parentSize.width < BreakpointWidthPx.Large
                ? 'sm'
                : 'lg'
            }
            onClick={handleRfqOrEysClick}
            fullbleed={
              parentSize.height < BreakpointHeightPx.Large ||
              parentSize.width < BreakpointWidthPx.Large
                ? true
                : false
            }
            style={
              parentSize.height < BreakpointHeightPx.Large ||
              parentSize.width < BreakpointWidthPx.Large
                ? {}
                : { width: 'fit-content' }
            }
            disabled={!rfqOrEysButtonEnabled}
            data-test-element-type={
              TestElementTypeId.GuidedViewGetConsultationButton
            }
          >
            <TdsIcon slot="icon" size="16px" name="message" />
          </TdsButton>
        )}
        <TdsButton
          variant="secondary"
          text={
            isFactoryModel
              ? t2(TextId.ACTION_LABEL_EXPLORE_DETAILS)
              : t2(TextId.ACTION_LABEL_CONFIGURE_TRUCK)
          }
          size={
            parentSize.height < BreakpointHeightPx.Large ||
            parentSize.width < BreakpointWidthPx.Large
              ? 'sm'
              : 'lg'
          }
          onClick={handleConfigurationClick}
          fullbleed={
            parentSize.height < BreakpointHeightPx.Large ||
            parentSize.width < BreakpointWidthPx.Large
              ? true
              : false
          }
          style={
            parentSize.height < BreakpointHeightPx.Large ||
            parentSize.width < BreakpointWidthPx.Large
              ? {}
              : { width: 'fit-content' }
          }
          data-test-element-type={
            TestElementTypeId.OverviewConfigureTruckButton
          }
        >
          <TdsIcon slot="icon" size="16px" name="filters" />
        </TdsButton>
      </ButtonsWrapper>
    </SpecificationRoot>
  )
}
